import { useState, useEffect } from "react";
import Card from "../../components/Card";
import { useLocation, useNavigate } from "react-router-dom";
import {
  createVendorBrand,
  deleteVendorBrand,
  deleteVendorSizingChart,
  getDefaultSizingCharts,
  getVendorBrands,
  getVendorSizingCharts,
  updateVendorBrand,
} from "../../services/products";
import { getProfile } from "../../services/vendor";
import { useAvStore } from "../../store";
import { getErrorMessage, sanitizePlainText } from "../../helpers/functions";
import ErrorMessage from "../../components/ErrorMessage";
import SizingChartsListTable from "../../components/setup/SizingChartsListTable";
import SizingChartsEmptyState from "../../components/setup/SizingChartsEmptyState";
import PrintIcon from "../../assets/svg/icon_print.svg?react";
import SizingChartsMockBanner from "../../components/setup/SizingChartsMockBanner";
import BrandTagContainer from "../../components/setup/BrandTagContainer";
import SizingChartConfirmModal from "../../components/setup/SizingChartConfirmModal";
import CustomSwitch from "../../components/CustomSwitch";
import EnableFitConf from "./modals/EnableFitConf";
import { CHART_TYPES } from "../../types/types";
import {
  getNormalizedVendorSizingCharts,
  toArrayPayload,
} from "../../helpers/vendorSizingChartsList";

export default function SizingCharts() {
  const navigate = useNavigate();
  const location = useLocation();
  const [brands, setBrands] = useState([]);
  const [charts, setCharts] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [brandPendingDelete, setBrandPendingDelete] = useState(null);
  const [brandCannotDelete, setBrandCannotDelete] = useState(null);
  const [chartPendingDelete, setChartPendingDelete] = useState(null);
  const [isDeletingChart, setDeletingChart] = useState(false);
  const [isDeletingBrand, setDeletingBrand] = useState(false);
  const [createdChartName, setCreatedChartName] = useState(
    location.state?.createdChartName || ""
  );
  const [fitMeta, setFitMeta] = useState({
    isFitEnabled: false,
    isEdited: false,
  });
  const [currentEnableConf, setCurrentEnableConf] = useState(null);

  useEffect(() => {
    loadAll();
  }, []);

  useEffect(() => {
    if (location.state?.createdChartName) {
      setCreatedChartName(location.state.createdChartName);
    }
  }, [location.state]);

  const loadAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [brandRes, chartRes, defaultChartRes, profileRes] =
        await Promise.all([
          getVendorBrands(),
          getVendorSizingCharts(),
          getDefaultSizingCharts(),
          getProfile(),
        ]);
      setBrands(toArrayPayload(brandRes?.data ?? brandRes));
      const rawCharts = chartRes?.data ?? chartRes;
      const rawDefaultChart = defaultChartRes?.data ?? defaultChartRes;
      const profilePayload = profileRes?.data ?? profileRes;
      const resolvedFitEnabled =
        typeof profilePayload?.isFitEnabled === "boolean"
          ? profilePayload.isFitEnabled
          : false;
      if (profilePayload && typeof profilePayload === "object") {
        useAvStore.getState().setUser(profilePayload);
      }
      const fitState = rawDefaultChart?.data ?? rawDefaultChart;
      setFitMeta({
        isFitEnabled: resolvedFitEnabled,
        isEdited:
          typeof fitState?.isEdited === "boolean" ? fitState.isEdited : false,
      });
      const list = getNormalizedVendorSizingCharts(rawCharts).filter((c) =>
        Boolean(c && (c.id || c.chartName))
      );
      if (import.meta.env.DEV) {
        console.info("[SizingCharts] raw list response:", rawCharts);
        console.info("[SizingCharts] normalized charts:", list);
      }
      setCharts(list);
    } catch (err) {
      setError(getErrorMessage(err));
      return false;
    } finally {
      setLoading(false);
    }
    return true;
  };

  const onCreateBrand = async ({ name }) => {
    const clean = sanitizePlainText(name, 120);
    if (!clean) return;
    try {
      await createVendorBrand({ name: clean });
      await loadAll();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const onRenameBrand = async ({ id, name }) => {
    const clean = sanitizePlainText(name, 120);
    if (!clean || !id) return;
    try {
      await updateVendorBrand({ id, data: { name: clean } });
      await loadAll();
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const onDeleteBrandRequest = (brand) => {
    setBrandPendingDelete(brand);
  };

  const confirmDeleteBrand = async () => {
    if (!brandPendingDelete?.id) return;
    setDeletingBrand(true);
    try {
      await deleteVendorBrand({ id: brandPendingDelete.id });
      setBrandPendingDelete(null);
      await loadAll();
    } catch (err) {
      // Backend blocks deletion when linked to chart/product.
      setBrandPendingDelete(null);
      setBrandCannotDelete(brandPendingDelete);
    } finally {
      setDeletingBrand(false);
    }
  };

  const confirmDeleteChart = async () => {
    if (!chartPendingDelete?.id) return;
    setDeletingChart(true);
    try {
      await deleteVendorSizingChart({ id: chartPendingDelete.id });
      setChartPendingDelete(null);
      await loadAll();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setDeletingChart(false);
    }
  };

  const goCreate = () => navigate("/sizing-charts/create");

  const hasBrands = brands.length > 0;
  const unit = charts[0]?.unit;
  const onFitRecommendationToggle = () => {
    setCurrentEnableConf(CHART_TYPES.DEFAULT);
  };

  return (
    <div className="container">
      <EnableFitConf
        isEdited={fitMeta.isEdited}
        isFitEnabled={fitMeta.isFitEnabled}
        isOpen={currentEnableConf}
        onClose={() => setCurrentEnableConf(null)}
        onSuccess={() => {
          const prevFitEnabled = fitMeta.isFitEnabled;
          const nextFitEnabled = !prevFitEnabled;
          setFitMeta((prev) => ({ ...prev, isFitEnabled: nextFitEnabled }));
          const user = useAvStore.getState().user;
          if (user) {
            useAvStore.getState().setUser({
              ...user,
              isFitEnabled: nextFitEnabled,
            });
          }
          loadAll().then((ok) => {
            if (ok === false) {
              setFitMeta((prev) => ({
                ...prev,
                isFitEnabled: prevFitEnabled,
              }));
              const u = useAvStore.getState().user;
              if (u) {
                useAvStore.getState().setUser({
                  ...u,
                  isFitEnabled: prevFitEnabled,
                });
              }
            }
          });
        }}
        category={currentEnableConf}
      />

      <SizingChartConfirmModal
        isOpen={Boolean(brandPendingDelete)}
        onClose={() => setBrandPendingDelete(null)}
        title="Delete Brand?"
        description={`Remove “${brandPendingDelete?.name ?? ""}”? This cannot be undone.`}
        confirmLabel="Delete Brand"
        onConfirm={confirmDeleteBrand}
        isLoading={isDeletingBrand}
        danger
      />

      <SizingChartConfirmModal
        isOpen={Boolean(brandCannotDelete)}
        onClose={() => setBrandCannotDelete(null)}
        title="Cannot delete brand"
        description={`“${brandCannotDelete?.name ?? ""}” is linked to one or more sizing charts/products. Remove related links before deleting this brand.`}
        confirmLabel="OK"
        showCancel={false}
        onConfirm={() => setBrandCannotDelete(null)}
        danger={false}
      />

      <SizingChartConfirmModal
        isOpen={Boolean(chartPendingDelete)}
        onClose={() => setChartPendingDelete(null)}
        title="Delete Sizing Chart?"
        description={`Delete “${chartPendingDelete?.chartName || ""}”? This cannot be undone.`}
        confirmLabel="Delete Chart"
        onConfirm={confirmDeleteChart}
        isLoading={isDeletingChart}
        danger
      />

      <SizingChartConfirmModal
        isOpen={Boolean(createdChartName)}
        onClose={() => {
          setCreatedChartName("");
          navigate(location.pathname, { replace: true, state: null });
        }}
        title="Sizing Chart Created"
        description={`“${createdChartName}” was created successfully.`}
        confirmLabel="Close"
        showCancel={false}
        onConfirm={() => {
          setCreatedChartName("");
          navigate(location.pathname, { replace: true, state: null });
        }}
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-8">
        <div>
          <h1 className="page-title mb-0">Sizing Charts</h1>
        </div>
      </div>

      <SizingChartsMockBanner />

      <ErrorMessage message={error} className="mb-4" />

      <div className="flex flex-col gap-8">
        <Card className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <h2 className="page-subtitle">Fit Recommendation</h2>
            {!isLoading && (
              <CustomSwitch
                name="fit-switch"
                title="Toggle Fit Recommendation"
                value={fitMeta.isFitEnabled}
                onChange={onFitRecommendationToggle}
              />
            )}
          </div>
          <p className="text-sm text-slate-600">
            When enabled, your active products will display a fit recommendation during try-on.
          </p>
        </Card>

        <Card className="flex flex-col gap-4">
          <div>
            <h2 className="page-subtitle">
              Brands
            </h2>
            <p className="text-sm text-slate-600">
              Brands organize your sizing charts and link them to the right products.
            </p>
          </div>
          <BrandTagContainer
            brands={brands}
            onCreateBrand={onCreateBrand}
            onRenameBrand={onRenameBrand}
            onDeleteRequest={onDeleteBrandRequest}
          />
        </Card>

        <section aria-labelledby="sizing-charts-heading">
          <Card className="flex flex-col gap-4 p-0 overflow-hidden">
            <div className="px-5 pt-5 pb-0 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h2
                  id="sizing-charts-heading"
                  className="page-subtitle"
                >
                  Sizing Charts
                </h2>
                <p className="text-sm text-slate-600 mt-1">
                  Sizing charts define the measurements used to recommend the right fit.
                </p>
              </div>
              <button
                type="button"
                className="btn-solid min-h-[48px] px-5 shrink-0"
                onClick={goCreate}
                disabled={!hasBrands}
                title={
                  !hasBrands
                    ? "Add at least one brand before creating a sizing chart"
                    : undefined
                }
              >
                Create Sizing Chart
              </button>
            </div>

            <div className="px-5 pb-5">
              {!isLoading && !error && charts.length === 0 ? (
                <SizingChartsEmptyState
                  onCreate={goCreate}
                  createDisabled={!hasBrands}
                />
              ) : (
                <div className="">
                  <SizingChartsListTable
                    items={charts}
                    isLoading={isLoading}
                    unit={unit}
                    brands={brands}
                    chartMeta={{}}
                    onDeleteRow={(row) => setChartPendingDelete(row)}
                  />
                </div>
              )}
            </div>
          </Card>
        </section>
      </div>
    </div>
  );
}
